/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
import { DELIVERY_SPEED, DeliverySpeedDetail } from "@swiggy-private/cart-types";
import { SCREEN_NAME, CLICK_ACTIONS, IMPRESSION_ACTIONS } from "../constants";
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

export const getDeliverySpeedEventContext = ({
    cartId,
    addressId,
    selected,
    source,
    text,
    variant,
    slaOptions,
    offerExpiry,
    offerMessage,
}: {
    cartId: number | string | undefined;
    addressId: string | undefined;
    selected?: DELIVERY_SPEED;
    source?: string;
    text?: string;
    variant?: string;
    slaOptions?: DeliverySpeedDetail[];
    offerExpiry?: number;
    offerMessage?: string;
}) => {
    return JSON.stringify({
        cart_id: String(cartId),
        address_id: String(addressId),
        selected,
        source,
        text,
        variant,
        slaOptions,
        offerExpiry,
        offerMessage,
        timeShown: !isEmpty(offerExpiry) && Number(offerExpiry) > 0 ? "Yes" : undefined,
    });
};

export const impressionCartDeliverySpeedPopup = ({
    impressionEvent,
    cartId,
    addressId,
    restaurantId,
}: {
    impressionEvent: AnalyticsContextValue["impressionEvent"];
    cartId: number | string | undefined;
    addressId: string | undefined;
    restaurantId?: string | number;
}): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_DELIVERY_TYPE_POPUP,
        context: getDeliverySpeedEventContext({ cartId, addressId }),
        label: String(restaurantId),
    });
};

export const impressionCartDeliverySpeed = ({
    impressionEvent,
    cartId,
    addressId,
    restaurantId,
    selected,
    source,
    slaOptions,
}: {
    impressionEvent: AnalyticsContextValue["impressionEvent"];
    cartId: number | string | undefined;
    addressId: string | undefined;
    restaurantId?: string | number;
    selected?: DELIVERY_SPEED;
    source?: string;
    slaOptions?: DeliverySpeedDetail[];
}): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_DELIVERY_TYPE,
        context: getDeliverySpeedEventContext({ cartId, addressId, selected, source, slaOptions }),
        label: String(restaurantId),
    });
};

export const impressionCartDeliverySpeedNudge = ({
    impressionEvent,
    cartId,
    addressId,
    restaurantId,
    source,
    variant,
}: {
    impressionEvent: AnalyticsContextValue["impressionEvent"];
    cartId: number | string | undefined;
    addressId: string | undefined;
    restaurantId?: string | number;
    source?: string;
    variant?: string;
}): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_DELIVERY_TYPE_NUDGE,
        context: getDeliverySpeedEventContext({ cartId, addressId, source, variant }),
        label: String(restaurantId),
    });
};

export const impressionOptInWidget = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId?: number | string,
    addressId?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_OPT_IN_WIDGET,
        context: getDeliverySpeedEventContext({ cartId, addressId }),
    });
};

export const impressionCartNoRushNudge = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: number | string | undefined,
    addressId: string | undefined,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_NORUSH_NUDGE,
        context: getDeliverySpeedEventContext({ cartId, addressId }),
    });
};

export const impressionExpressBuyBlckNudge = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    context: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_EXPRESS_BUY_BLCK_NUDGE,
        context,
    });
};

export const clickDeliverySpeedItem = ({
    clickEvent,
    cartId,
    addressId,
    type,
    restaurantId,
}: {
    clickEvent: AnalyticsContextValue["clickEvent"];
    cartId: number | string | undefined;
    addressId: string | undefined;
    type: DELIVERY_SPEED;
    restaurantId?: string | number;
}): void => {
    let action = CLICK_ACTIONS.CLICK_CART_STANDARD;
    if (type === DELIVERY_SPEED.BATCH_NO_RUSH) {
        action = CLICK_ACTIONS.CLICK_CART_NORUSH;
    } else if (type === DELIVERY_SPEED.EXPRESS) {
        action = CLICK_ACTIONS.CLICK_EXPRESS_DELIVERY;
    }

    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action,
        context: getDeliverySpeedEventContext({ cartId, addressId, selected: type }),
        label: String(restaurantId),
    });
};

export const clickCartNoRushNudge = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId: number | string | undefined,
    addressId: string | undefined,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_CART_NORUSH_NUDGE,
        context: getDeliverySpeedEventContext({ cartId, addressId }),
    });
};

export const clickExpressBuyBlckNudge = (clickEvent: AnalyticsContextValue["clickEvent"], context: string): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_EXPRESS_BUY_BLCK_NUDGE,
        context,
    });
};

export const impressionDeliveryTypeOffer =
    (
        cartId: number | string | undefined,
        addressId: string | undefined,
        restaurantId?: number | string | undefined,
        selected?: DELIVERY_SPEED,
    ) =>
    (impressionEvent: AnalyticsContextValue["impressionEvent"], offerMessage?: string, offerExpiry?: number) => {
        impressionEvent({
            category: SCREEN_NAME.CHECKOUT,
            action: IMPRESSION_ACTIONS.IMPRESSION_DELIVERY_TYPE_OFFER,
            context: getDeliverySpeedEventContext({
                cartId,
                addressId,
                offerExpiry,
                offerMessage,
                selected,
            }),
            label: String(restaurantId),
        });
    };
export const impressionDeliveryTypeOfferExpired =
    (
        cartId: number | string | undefined,
        addressId: string | undefined,
        restaurantId?: number | string | undefined,
        selected?: DELIVERY_SPEED,
    ) =>
    (impressionEvent: AnalyticsContextValue["impressionEvent"]) => {
        impressionEvent({
            category: SCREEN_NAME.CHECKOUT,
            action: IMPRESSION_ACTIONS.IMPRESSION_DELIVERY_TYPE_OFFER_EXPIRED,
            context: getDeliverySpeedEventContext({ cartId, addressId, selected }),
            label: String(restaurantId),
        });
    };

export const impressionEcoSaverToast = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: number | string | undefined,
    message: string | undefined,
    addressId: string | undefined,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_ECO_SAVER_TOAST_MESSAGE,
        context: getDeliverySpeedEventContext({ cartId, text: message, addressId }),
        label: String(cartId),
    });
};

export const impressionEcoSaverLottie = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: number | string | undefined,
    addressId: string | undefined,
    lottieId: string | undefined,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_NO_RUSH_LOTTIE,
        context: JSON.stringify({ cartId, addressId, lottieId }),
        label: String(cartId),
    });
};

export const clickEcoSaverToast = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId: number | string | undefined,
    message: string | undefined,
    addressId: string | undefined,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        action: CLICK_ACTIONS.CLICK_ECO_SAVER_TOAST_MESSAGE,
        context: getDeliverySpeedEventContext({ cartId, text: message, addressId }),
        label: String(cartId),
    });
};
